import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    openAjuaModal: () => void;
    loadAjuaWebform: (options: any) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AjuaService {
  private scriptLoaded = false;
  private allowedRoutes: string[] = ['/milele-trust/success', '/estate-planning/success'];

  constructor(
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  public initialize(routes?: string[]): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (routes && routes.length > 0) {
      this.allowedRoutes = routes;
    }

    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (this.isRouteAllowed(event.urlAfterRedirects)) {
          this.loadScript();
        } else {
          this.unloadFeedback();
        }
      });
  }

  private isRouteAllowed(currentUrl: string): boolean {
    return this.allowedRoutes.some(route => currentUrl.startsWith(route));
  }

  private loadScript(): void {
    if (this.scriptLoaded) {
      this.triggerAjuaWidget();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://feedback.ajua.com/embed/v1/embed.js';
    script.async = true;
    script.onload = () => {
      this.scriptLoaded = true;
      this.triggerAjuaWidget();
    };
    
    document.body.appendChild(script);
  }

  /**
   * This mimics the 'onload' behavior in your HTML file
   */
  private triggerAjuaWidget(): void {
    if (window.loadAjuaWebform) {
      window.loadAjuaWebform({
        joinCode: environment.ajuaCode,
        widget: true,
        widgetPosition: 'middle-right',
        widgetText: 'Feedback',
        widgetIcon: '💬',
        widgetColor: '#ffffff',
        widgetBackground: '#0c5daa',
        gap: 8,
        delay: 1500, // slight delay for Angular view rendering
        rounded: true
      });
    }
  }

  /**
   * Manually open the modal (useful for custom buttons in your components)
   */
  public openManualModal(): void {
    if (window.openAjuaModal) {
      window.openAjuaModal();
    } else {
      console.warn('Ajua modal not ready yet');
    }
  }

  private unloadFeedback(): void {
    // To properly "unload", we usually hide the widget container
    // Ajua creates its own elements, so we look for their classes/IDs
    const widget = document.querySelector('.ajua-widget-container'); // Check actual class in inspector
    if (widget) {
      (widget as HTMLElement).style.display = 'none';
    }
  }
}